/*
Copyright (C) 2018 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { Dispatch, FC, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { get, isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';

import type { CollectionStateHook } from '@broadleaf/admin-components/dist/collection/hooks/useCollectionState';
import useEventCallback from '@broadleaf/admin-components/dist/common/hooks/useEventCallback';
import log from '@broadleaf/admin-components/dist/common/utils/log';
import {
  getAttribute,
  getEndpointByType
} from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import {
  CreateReturnContext,
  useContextParamsForCreateReturnAction as useContextParams
} from '@broadleaf/admin-components/dist/oms/elements/CreateReturnAction';
import type {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import type {
  IOrderFulfillment,
  IOrderLocalState
} from '@broadleaf/admin-components/dist/types/oms';
import type { IFormik } from '@broadleaf/admin-components/dist/types/form';
import { CreateReturnAction } from 'app/oms/elements/CreateReturnAction';

const logger = log.getLogger(
  'oms.components.ItemsOrderViewSection.FulfillmentCard.action-components.CreateReturn'
);

export interface FulfillmentCardCreateReturnProps {
  actionDefinition: IMetadataAction;
  className?: string;
  fulfillment: IOrderFulfillment;
  fulfillmentsState: CollectionStateHook;
  orderDispatch: Dispatch<any>;
  orderMetadata: IMetadata;
  orderState: IOrderLocalState;
  handleSubmit?: (
    values: any,
    formikBag: IFormik,
    submit: (values: any, formikBag: IFormik) => void
  ) => void;
  children?: React.ReactNode;
}

/**
 * This action allows creating a `ReturnAuthorization` from a single
 * fulfillment's items.
 */
export const FulfillmentCardCreateReturn: FC<
  FulfillmentCardCreateReturnProps
> = ({
  actionDefinition,
  className,
  fulfillment,
  fulfillmentsState,
  orderDispatch: dispatch,
  orderMetadata,
  orderState,
  handleSubmit,
  children
}) => {
  const { id: orderId } = useParams<Record<string, any>>();
  const allowedStatuses = new Set(
    getAttribute(actionDefinition, 'allowedStatuses', [])
  );
  const disabled = fulfillmentsState.state.isFetching;
  const submitEndpoint = getEndpointByType(actionDefinition, 'CREATE_RETURN');
  const readItemsEndpoint = getEndpointByType(
    actionDefinition,
    'READ_RETURNABLE_ITEMS'
  );
  const contextParams = useContextParams(
    orderMetadata,
    orderState,
    fulfillment.id,
    fulfillment
  );
  const [returnableItems, setReturnableItems] = useState(undefined);
  const [fetchingReturnableItems, setFetchingReturnableItems] = useState(true);
  const handleSuccess = useEventCallback(async () => {
    // trigger a refresh of the fulfillments collection
    await fulfillmentsState.handleRead();
    setReturnableItems(undefined);
  }, [fulfillmentsState.handleRead]);

  useEffect(() => {
    if (!orderState.data || returnableItems !== undefined) {
      return;
    }

    (async () => {
      setFetchingReturnableItems(true);
      try {
        const response = await request(
          { method: 'get', ...readItemsEndpoint },
          contextParams
        );
        setReturnableItems(response.data);
      } catch (err) {
        logger.error(
          `An error occurred trying to fetch returnable items for order ${orderId}`,
          err
        );
        setReturnableItems([]);
      } finally {
        setFetchingReturnableItems(false);
      }
    })();
  }, [contextParams, orderId, orderState, readItemsEndpoint, returnableItems]);

  const context = useMemo(
    () => ({
      returnableItems,
      fetchingReturnableItems,
      fulfillment,
      order: get(orderState, 'data', {}),
      dispatch,
      disabled,
      submitEndpoint,
      contextParams
    }),
    [
      dispatch,
      fulfillment,
      returnableItems,
      orderState,
      fetchingReturnableItems,
      submitEndpoint,
      contextParams,
      disabled
    ]
  );

  if (!allowedStatuses.has(fulfillment.status) || isEmpty(returnableItems)) {
    return null;
  }

  return (
    <CreateReturnContext.Provider value={context}>
      <CreateReturnAction
        actionDefinition={actionDefinition}
        className={cx(
          'tw-w-full tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-px-4 tw-py-4 tw-text-sm tw-font-semibold tw-text-red-700 tw-shadow',
          'hover:tw-bg-red-100 focus:tw-bg-red-100 focus:tw-outline-none focus:tw-ring active:tw-bg-red-200 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-200 disabled:tw-text-gray-700',
          'md:tw-py-2',
          'tw-mb-3', //   Aurora Customization to make space for extra buttons
          { [className]: !!className }
        )}
        handleSuccess={handleSuccess}
        handleSubmit={handleSubmit}
        children={children}
      />
    </CreateReturnContext.Provider>
  );
};

export default FulfillmentCardCreateReturn;
